import { api } from "./api";
import { ListRequest, MessageResponse } from "../types/common/commonTypes";

import {
  AddressCreate,
  AddressUpdate,
  AddressFull,
  PaginatedAddressResponse,
  WorldLocationResponse,
  WorldCityCreate,
  WorldCitySchema,
  WorldStateCreate,
  WorldStateSchema,
  WorldCountryCreate,
  WorldCountrySchema,
} from "../types/addressTypes";

import { PaginatedResponse } from "../types/common/commonTypes";

/**
 * Inventory Management Service
 *
 * Provides complete integration with all 12 inventory management API endpoints:
 * - Core lot management (CRUD operations)
 * - FIFO allocation engine
 * - Real-time availability tracking
 * - Lot shipping and movement
 * - Comprehensive inventory summaries
 *
 * Auto-generation: Lot numbers are auto-generated by backend (YY-###### format)
 * Example: 24-000001, 24-000002, etc. (resets annually)
 */
const addr_base = "/addresses";
export const addressService = {
  // ========================================
  // Core Inventory Lot Management (6 endpoints)
  // ========================================

  /**
   * Create a new inventory lot
   * POST /api/v1/inventory/lots
   * Note: Lot numbers are auto-generated by backend (format: YY-######)
   */
  create: async (data: AddressCreate): Promise<AddressFull> => {
    const response = await api.post(`${addr_base}/`, data);
    return response.data;
  },
  list: async (params: ListRequest): Promise<PaginatedAddressResponse> => {
    const response = await api.post(`${addr_base}/search`, params);
    return response.data;
  },
  getWorldLocationsCountries: async (): Promise<
    PaginatedResponse<WorldCountrySchema>
  > => {
    const response = await api.get(`${addr_base}/worldlocations/country`);
    return response.data;
  },
  getWorldLocationsStates: async (): Promise<
    PaginatedResponse<WorldStateSchema>
  > => {
    const response = await api.get(`${addr_base}/worldlocations/state`);
    return response.data;
  },
  getWorldLocationsCities: async (): Promise<
    PaginatedResponse<WorldCitySchema>
  > => {
    const response = await api.get(`${addr_base}/worldlocations/city`);
    return response.data;
  },
  createCountry: async (
    data: WorldCountryCreate,
  ): Promise<WorldCountrySchema> => {
    const response = await api.post(
      `${addr_base}/worldlocations/country`,
      data,
    );
    return response.data;
  },
  createState: async (data: WorldStateCreate): Promise<WorldStateSchema> => {
    const response = await api.post(`${addr_base}/worldlocations/state`, data);
    return response.data;
  },
  createCity: async (data: WorldCityCreate): Promise<WorldCitySchema> => {
    const response = await api.post(`${addr_base}/worldlocations/city`, data);
    return response.data;
  },

  /**
   * Get a specific inventory lot by ID
   * GET /api/v1/inventory/lots/{lot_id}
   */
  getById: async (id: string): Promise<AddressFull> => {
    const response = await api.get(`${addr_base}/${id}`);
    return response.data;
  },

  /**
   * Update an inventory lot
   * PUT /api/v1/inventory/lots/{lot_id}
   */
  update: async (id: string, data: AddressUpdate): Promise<AddressFull> => {
    const response = await api.put(`${addr_base}/${id}`, { ...data, id });
    return response.data;
  },

  /**
   * Delete an inventory lot (soft delete)
   * DELETE /api/v1/inventory/lots/{lot_id}
   */
  deactivate: async (id: string): Promise<MessageResponse> => {
    const response = await api.delete(`${addr_base}/${id}`);
    return response.data;
  },

  /**
   * Restore a deleted inventory lot
   * POST /api/v1/inventory/lots/{lot_id}/restore
   */
  reactivate: async (id: string): Promise<AddressFull> => {
    const response = await api.post(`${addr_base}/${id}/restore`);
    return response.data;
  },
};
